import React from 'react';
import PropTypes from 'prop-types';
import SidePanel from '../SidePanel';

/**
 * «Se orderdetaljer» — the order's lines and totals in a DS SidePanel. Pure presentation: the host hands in
 * what to show (the webshop wrapper reads its cart, OVER/LL its server cart).
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {node}     props.title        - panel title (localized)
 * @param {Array}    props.sections     - [{ heading, items: [{ id, name, quantity, unitPrice }] }]; an empty section is not rendered
 * @param {Array}    [props.rows]       - summary rows [{ key, label, value, variant?: 'discount' }], values pre-formatted
 * @param {node}     props.totalLabel
 * @param {string}   props.totalDisplay - pre-formatted total
 * @param {Function} props.formatPrice  - (number) => string, for the line prices
 * @param {boolean}  [props.floating]   - DS SidePanel `floating` (desktop: inset + rounded; OVER/LL's drawers)
 */
const OrderDetailsPanel = ({ isOpen, onClose, title, sections = [], rows = [], totalLabel, totalDisplay, formatPrice, floating = false }) => {
  const fmt = (v) => formatPrice(Number(v) || 0);

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={title} floating={floating}>
      <div className="checkout__details">
        {sections.filter((s) => s.items?.length > 0).map((section) => (
          <div className="checkout__details-section" key={section.heading}>
            <h5 className="checkout__details-heading typo-h5">{section.heading}</h5>
            <div className="checkout__details-list">
              {section.items.map((item) => (
                <div className="checkout__details-item" key={item.id}>
                  <div className="checkout__details-item-info">
                    <span className="typo-body">{item.name}</span>
                    <span className="checkout__details-item-qty typo-body-sm">
                      {item.quantity} × {fmt(item.unitPrice)}
                    </span>
                  </div>
                  <span className="typo-body">{fmt(item.unitPrice * item.quantity)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="checkout__details-summary">
          {rows.map((row) => (
            <div
              key={row.key || row.label}
              className={`checkout__details-row${row.variant === 'discount' ? ' checkout__details-row--discount' : ''}`}
            >
              <span className="typo-body">{row.label}</span>
              <span className="typo-body">{row.value}</span>
            </div>
          ))}
          <div className="checkout__details-divider" />
          <div className="checkout__details-row checkout__details-row--total">
            <span className="typo-h3">{totalLabel}</span>
            <span className="typo-h3">{totalDisplay}</span>
          </div>
        </div>
      </div>
    </SidePanel>
  );
};

OrderDetailsPanel.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.node,
  sections: PropTypes.arrayOf(PropTypes.shape({
    heading: PropTypes.node,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.node,
      quantity: PropTypes.number,
      unitPrice: PropTypes.number,
    })),
  })),
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.node,
    value: PropTypes.node,
    variant: PropTypes.oneOf(['discount']),
  })),
  totalLabel: PropTypes.node,
  totalDisplay: PropTypes.node,
  formatPrice: PropTypes.func.isRequired,
  floating: PropTypes.bool,
};

export default OrderDetailsPanel;
